import { MouseEvent, ReactNode } from 'react';

import clsx from 'clsx';

import { ConfirmationDialogResult } from './types';
import { Button } from '../../../../components/buttons';
import { Scroller, ScrollerProps } from '../../../../components/scroller';
import { tokens } from '../../../../theme/generated/tokens';
import { classNamesFor } from '../../../../utils/classNames';
import { Inset, ActionsGroup } from '../../../containers';
import { TypographyOrNode } from '../../../utils';
import { DialogCard, DialogCardProps } from '../../components/DialogCard';
import { DialogFooter, DialogFooterProps } from '../../components/DialogFooter';
import { DialogFootnoteProps } from '../../components/DialogFootnote';
import { DialogHeader, DialogHeaderProps } from '../../components/DialogHeader';
import { useCloseDialog } from '../../hooks/useCloseDialog';

const classNamesFactory = classNamesFor('ConfirmationDialog');

const classNames = {
  root: classNamesFactory.root(),
  title: classNamesFactory.element('Title'),
  message: classNamesFactory.element('Message'),
  cancelButton: classNamesFactory.element('CancelButton'),
  confirmButton: classNamesFactory.element('ConfirmButton'),
};

export type ConfirmationDialogProps<RESULT = unknown> = {
  /**
   * Dialog title, displayed at the start of the header
   */
  title: ReactNode;
  /**
   * Metadata content displayed below the title in the header
   */
  metadata?: ReactNode;
  /**
   * Message of the dialog. If a string is passed, it is wrapped with `Typography`
   */
  message: ReactNode;
  /**
   * Text, to be displayed on the confirmation button
   */
  confirmButtonText?: string;
  /**
   * Text, to be displayed on the cancel button
   */
  cancelButtonText?: string;
  /**
   * Should the dialog be closed, on clicking the confirmation button
   */
  closeOnConfirm?: boolean;
  /**
   * Callback to be invoked on clicking the confirmation button
   */
  onConfirm: (event: MouseEvent) => void;
  /**
   * Should the dialog be closed, on clicking the cancel button
   */
  closeOnCancel?: boolean;
  /**
   * Callback to be invoked on clicking the cancel button
   */
  onCancel?: (event: MouseEvent) => void;
  /**
   * Should divider be displayed at the top of the footer
   * @default false
   */
  footerDivider?: boolean;
  testId?: string;
  slotProps?: {
    root?: DialogCardProps;
    header?: DialogHeaderProps;
    scroller?: ScrollerProps;
    footer?: DialogFooterProps;
    footnote?: DialogFootnoteProps;
  };
} & DialogCardProps<RESULT>;

/**
 * Confirmation dialog variant with mandatory title, message and footer with confirm and cancel button
 */
export const ConfirmationDialog = <RESULT = unknown,>({
  title,
  metadata,
  message,
  confirmButtonText = 'Ok',
  cancelButtonText = 'Cancel',
  closeOnConfirm = true,
  onConfirm,
  closeOnCancel = true,
  onCancel,
  footerDivider = false,
  slotProps,
  result,
  testId = 'confirmation-dialog',
}: ConfirmationDialogProps<RESULT>) => {
  const closeDialog = useCloseDialog();
  return (
    <DialogCard
      testId={testId}
      {...slotProps?.root}
      result={result}
      className={clsx(classNames.root, slotProps?.root?.className)}
    >
      <DialogHeader
        title={title}
        metadata={metadata}
        slotProps={{ startContentWrapper: { className: classNames.title } }}
        {...slotProps?.header}
      />
      <Scroller {...slotProps?.scroller}>
        <Inset py={false}>
          <TypographyOrNode
            variant="P12R"
            className={classNames.message}
            sx={{ color: tokens.theme.fg.structural.secondary }}
          >
            {message}
          </TypographyOrNode>
        </Inset>
      </Scroller>
      <DialogFooter
        divider={footerDivider}
        endContent={
          <ActionsGroup>
            <Button
              variant="secondary"
              testId="cancel-button"
              className={classNames.cancelButton}
              onClick={(event) => {
                if (closeOnCancel) {
                  closeDialog(ConfirmationDialogResult.CANCELED);
                }
                onCancel?.(event);
              }}
            >
              {cancelButtonText}
            </Button>
            <Button
              testId="confirm-button"
              className={classNames.confirmButton}
              onClick={(event) => {
                if (closeOnConfirm) {
                  closeDialog(ConfirmationDialogResult.CONFIRMED);
                }
                onConfirm?.(event);
              }}
            >
              {confirmButtonText}
            </Button>
          </ActionsGroup>
        }
        {...slotProps?.footer}
      />
    </DialogCard>
  );
};

ConfirmationDialog.classNames = classNames;
